/**
 * annot-surface.js — где человек рисует: превью картинки, ввод мышью, пером и
 * пальцем, текст, клавиши и панель инструментов.
 *
 * Два режима одной поверхности:
 *   'frames'  — превью рисует она сама из кадров, ушедших в файл
 *               (layer.previewSource(): кадр захвата после «Области» и
 *               подгонки, или композит с кружком камеры). Так человек рисует
 *               ровно по тому кадру, который пишется, а пометка попадает в
 *               файл в ту же точку. /screen-recorder.
 *   'overlay' — прозрачный холст поверх готового <video> (превью ведущего
 *               /screen-share): картинку показывает видео, мы — только
 *               пометки и стоп-кадр.
 *
 * Клавиши — handleKey(e): страница зовёт его ДО своих горячих клавиш и
 * отдаёт клавишу, только если поверхность её не взяла. Буквы — через
 * __keyLetter (русская раскладка), знаки — __keySign; цвета — цифрами,
 * потому что R и P у записи уже заняты («Стоп» и «Пауза»), и одна буква с
 * двумя смыслами в двух режимах — ровно та ошибка режима, которая стоит дубля.
 *
 * Разбор — docs/why/primitives/screen-annotate.md.
 */
(function () {
    'use strict';
    if (window.ScreenAnnotSurface) return;
    var C = window.ScreenAnnotCore;

    var TOOL_KEYS = { b: 'pen', h: 'hl', l: 'laser', a: 'arrow', s: 'rect', o: 'oval', t: 'text', n: 'step', x: 'blur', e: 'eraser', g: 'spot', z: 'zoom' };
    var HOLD_MS = 450;          // столько держать перо неподвижным, чтобы штрих выпрямился
    var HOLD_SLACK = 0.004;     // «неподвижно» — в долях картинки

    function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }
    function dims(src) {
        if (!src) return null;
        if (typeof src.displayWidth === 'number' && src.displayWidth) return { w: src.displayWidth, h: src.displayHeight };
        if (src.tagName === 'VIDEO') return src.videoWidth ? { w: src.videoWidth, h: src.videoHeight } : null;
        if (typeof src.width === 'number' && src.width) return { w: src.width, h: src.height };
        return null;
    }

    function mount(o) {
        var layer = o.layer, stage = o.stage, mode = o.mode === 'overlay' ? 'overlay' : 'frames';
        var bar = o.bar || null, slug = o.slug || 'screen-recorder';
        var hide = o.hideTools || [];
        // Окно, где живёт поверхность: страница или окно «поверх программ»
        // (Document Picture-in-Picture). Там свои кадры анимации, свой
        // devicePixelRatio и свой документ — страница под ним скрыта.
        var win = o.win || window, doc = win.document;
        var TS = window.__toolSettings;
        function save(k, v) { try { if (TS) TS.save(slug, 'annot_' + k, v); } catch (_) {} }
        function load(k, d) { try { return TS ? TS.load(slug, 'annot_' + k, d) : d; } catch (_) { return d; } }

        var canvas = doc.createElement('canvas');
        canvas.className = 'sa-canvas';
        canvas.setAttribute('aria-hidden', 'true');
        stage.appendChild(canvas);
        // Слой ровно над картинкой (без полей вписывания): прокрутка вкладки
        // колёсиком (CaptureController.forwardWheel) пересчитывает точку по
        // коробке ЭЛЕМЕНТА, и поля сдвинули бы её. Рисование слушает и его.
        var pad = doc.createElement('div');
        pad.className = 'sa-pad';
        pad.setAttribute('aria-hidden', 'true');
        stage.appendChild(pad);
        var ctx = canvas.getContext('2d');
        var on = false, raf = 0, dead = false, dpr = 1, cw = 0, ch = 0;
        var R = { x: 0, y: 0, w: 1, h: 1 }, aspect = 16 / 9, padKey = '';
        var cur = null, erasing = false, snapped = false, holdT = 0, holdAt = null;
        var editor = null, needPaint = true, zdrag = null, prevTool = 'pen';
        var ws = null;

        // ── размер холста ────────────────────────────────────────────────
        function resize() {
            var r = stage.getBoundingClientRect();
            dpr = Math.min(2, win.devicePixelRatio || 1);
            var w = Math.max(1, Math.round(r.width * dpr)), h = Math.max(1, Math.round(r.height * dpr));
            if (w !== canvas.width || h !== canvas.height) { canvas.width = w; canvas.height = h; needPaint = true; }
            cw = w; ch = h;
        }
        var ro = null;
        try { ro = new win.ResizeObserver(function () { resize(); kick(); }); ro.observe(stage); } catch (_) { win.addEventListener('resize', resize); }
        resize();

        // ── картинка → прямоугольник на холсте ──────────────────────────
        function picture() {
            if (mode === 'overlay') return o.media ? dims(o.media) : null;
            return dims(layer.previewSource());
        }
        function layout() {
            var dm = picture();
            if (dm) aspect = dm.w / dm.h;
            R = C.fitRect(cw, ch, dm ? dm.w : cw, dm ? dm.h : ch);
            // слой прокрутки — по картинке; стиль пишется, только когда она сдвинулась
            var key = Math.round(R.x / dpr) + ',' + Math.round(R.y / dpr) + ',' + Math.round(R.w / dpr) + ',' + Math.round(R.h / dpr);
            if (key !== padKey) {
                padKey = key;
                pad.style.left = Math.round(R.x / dpr) + 'px'; pad.style.top = Math.round(R.y / dpr) + 'px';
                pad.style.width = Math.round(R.w / dpr) + 'px'; pad.style.height = Math.round(R.h / dpr) + 'px';
            }
        }

        // ── отрисовка превью ─────────────────────────────────────────────
        function paint() {
            if (dead) return;
            raf = 0;
            resize();
            layout();
            ctx.clearRect(0, 0, cw, ch);
            var z = layer.zoomRect();
            if (mode === 'frames') {
                ctx.fillStyle = '#05070d';
                ctx.fillRect(0, 0, cw, ch);
                var src = layer.previewSource(), sd = dims(src);
                // Основа композитора уже несёт лупу и пометки — её не приближаем второй раз.
                var inked = layer.baseHasInk();
                if (src && sd) { try { C.drawZoomed(ctx, src, sd.w, sd.h, inked ? null : z, R.x, R.y, R.w, R.h); } catch (_) {} }
                if (!inked) layer.renderInto(ctx, R, { mosaic: true });
            } else {
                var media = o.media;
                if (media && media.readyState >= 2 && media.videoWidth) layer.noteSource(media);
                var fz = layer.frozenCanvas();
                var base = fz || (z && media && media.videoWidth ? media : null), bd = dims(base);
                // Без лупы живую картинку показывает само видео — холст прозрачен.
                if (base && bd) { try { C.drawZoomed(ctx, base, bd.w, bd.h, z, R.x, R.y, R.w, R.h); } catch (_) {} }
                layer.renderInto(ctx, R, { mosaic: false });
            }
            var d = layer.drawing();
            if (d && d.k === 'blur') C.renderGuides(ctx, R, [d]);
            if (zdrag) paintZoomGuide();
            needPaint = false;
            if (live()) kick();
        }
        function paintZoomGuide() {
            var r = zoomView(zdrag);
            if (!r) return;
            var x = R.x + r.x * R.w, y = R.y + r.y * R.h, w = r.w * R.w, h = r.h * R.h;
            ctx.save();
            ctx.fillStyle = 'rgba(0,0,0,.45)';
            ctx.fillRect(R.x, R.y, R.w, y - R.y);
            ctx.fillRect(R.x, y + h, R.w, R.y + R.h - y - h);
            ctx.fillRect(R.x, y, x - R.x, h);
            ctx.fillRect(x + w, y, R.x + R.w - x - w, h);
            ctx.setLineDash([7, 5]); ctx.lineWidth = 2; ctx.strokeStyle = '#fff';
            ctx.strokeRect(x, y, w, h);
            ctx.restore();
        }
        // Нужен ли следующий кадр превью: рисуют, что-то гаснет, идёт живая картинка.
        function live() {
            if (dead || doc.visibilityState === 'hidden') return false;
            if (mode === 'frames') return on || layer.frozen();
            return on || layer.items().length > 0 || layer.frozen() || !!layer._state().ptr || layer.hasRemote() || !!layer.zoomRect();
        }
        function kick() { if (!raf && !dead) raf = win.requestAnimationFrame(paint); }
        var unlisten = layer.onChange(function () { needPaint = true; kick(); });
        doc.addEventListener('visibilitychange', kick);

        // ── лупа: обведённый участок вида (доли R), форма — как у кадра ──
        function zoomView(zd) {
            var dx = zd.x1 - zd.x0, dy = zd.y1 - zd.y0;
            var side = Math.max(Math.abs(dx), Math.abs(dy));
            if (side < 0.03) return null;
            var x = dx < 0 ? zd.x0 - side : zd.x0, y = dy < 0 ? zd.y0 - side : zd.y0;
            side = Math.min(side, 1);
            return { x: clamp(x, 0, 1 - side), y: clamp(y, 0, 1 - side), w: side, h: side };
        }
        function zoomFinish(zd) {
            var v = zoomView(zd);
            // щелчок без протяжки — вдвое ближе вокруг точки
            if (!v) v = { x: clamp(zd.x0 - 0.25, 0, 0.5), y: clamp(zd.y0 - 0.25, 0, 0.5), w: 0.5, h: 0.5 };
            layer.setZoom(C.zoomCompose(layer.zoomed() ? layer._state().zoom.to : null, v), 'select');
            // приблизили — рисовать дальше прежним инструментом
            pickTool(prevTool && prevTool !== 'zoom' ? prevTool : 'pen', 'zoom');
        }
        function zoomOut(via) {
            if (!layer.zoomed()) return false;
            layer.setZoom(null, via || 'out');
            paintBar();
            return true;
        }

        // ── ввод ─────────────────────────────────────────────────────────
        function pt(ev) {
            var r = canvas.getBoundingClientRect();
            return C.toNorm(R, (ev.clientX - r.left) * dpr, (ev.clientY - r.top) * dpr, 0.02);
        }
        function cursor() {
            var k = layer.tool();
            var c = !on ? '' : (k === 'laser' || k === 'spot') ? 'none' : k === 'text' ? 'text' : k === 'eraser' ? 'cell' : k === 'zoom' ? 'zoom-in' : 'crosshair';
            canvas.style.cursor = c; pad.style.cursor = c;
        }
        function armHold() {
            if (holdT) clearTimeout(holdT);
            holdT = 0;
            if (!cur || cur.k !== 'pen' || snapped) return;
            holdT = setTimeout(function () {
                holdT = 0;
                if (!cur || cur.k !== 'pen' || snapped) return;
                var rec = C.recognize(cur.p, aspect);
                if (rec) { layer.reshape(cur, rec.k, rec.p); snapped = true; }
            }, HOLD_MS);
        }
        function onDown(ev) {
            if (!on || editor) return;
            if (ev.button !== undefined && ev.button !== 0 && ev.pointerType === 'mouse') return;
            var p = pt(ev);
            if (!p) return;
            ev.preventDefault();
            try { ev.currentTarget.setPointerCapture(ev.pointerId); } catch (_) {}
            var k = layer.tool();
            if (k === 'zoom') { zdrag = { x0: p.x, y0: p.y, x1: p.x, y1: p.y }; kick(); return; }
            if (k === 'eraser') { erasing = true; layer.eraseAt(p.x, p.y, aspect); return; }
            if (k === 'spot') { layer.pointer(p.x, p.y); return; }
            if (k === 'text') { openEditor(p, ev); return; }
            if (k === 'step') { var st = layer.begin(p.x, p.y); layer.end(st); return; }
            if (k === 'laser') layer.pointer(p.x, p.y);
            cur = layer.begin(p.x, p.y);
            snapped = false;
            holdAt = p;
            armHold();
        }
        function onMove(ev) {
            if (!on) return;
            var k = layer.tool();
            if (zdrag) { var zp = pt(ev); if (zp) { zdrag.x1 = zp.x; zdrag.y1 = zp.y; kick(); } return; }
            var evs = (cur && typeof ev.getCoalescedEvents === 'function') ? ev.getCoalescedEvents() : null;
            if (!evs || !evs.length) evs = [ev];
            var last = null;
            for (var i = 0; i < evs.length; i++) {
                var p = pt(evs[i]);
                if (!p) continue;
                last = p;
                if (cur && !snapped) layer.extend(cur, p.x, p.y, ev.shiftKey, aspect);
            }
            if (!last) { if (k === 'laser' || k === 'spot') layer.pointer(null); return; }
            if (k === 'laser' || k === 'spot') layer.pointer(last.x, last.y);
            if (erasing) layer.eraseAt(last.x, last.y, aspect);
            if (cur && holdAt && (Math.abs(last.x - holdAt.x) > HOLD_SLACK || Math.abs(last.y - holdAt.y) > HOLD_SLACK)) {
                holdAt = last;
                armHold();
            }
        }
        function onUp() {
            if (holdT) { clearTimeout(holdT); holdT = 0; }
            erasing = false;
            if (zdrag) { var zd = zdrag; zdrag = null; zoomFinish(zd); kick(); }
            if (cur) { layer.end(cur); cur = null; }
            snapped = false;
        }
        function onLeave() {
            if (cur || zdrag) return;   // захват указателя держит штрих — уход за край не конец
            var k = layer.tool();
            if (k === 'laser' || k === 'spot') layer.pointer(null);
        }
        function onWheel(ev) {
            if (!on || layer.tool() !== 'spot') return;
            ev.preventDefault();
            // колёсико у прожектора — его радиус, а не прокрутка вкладки
            if (ev.stopImmediatePropagation) ev.stopImmediatePropagation();
            layer.spotRadius(layer.spotRadius() * (ev.deltaY > 0 ? 0.9 : 1.1));
        }
        [canvas, pad].forEach(function (el) {
            el.addEventListener('pointerdown', onDown);
            el.addEventListener('pointermove', onMove);
            el.addEventListener('pointerup', onUp);
            el.addEventListener('pointercancel', onUp);
            el.addEventListener('pointerleave', onLeave);
            el.addEventListener('wheel', onWheel, { passive: false });
            el.addEventListener('contextmenu', function (e) { if (on) e.preventDefault(); });
        });

        // ── текст ────────────────────────────────────────────────────────
        function openEditor(p) {
            var it = layer.begin(p.x, p.y);
            if (!it) return;
            var ta = doc.createElement('textarea');
            ta.className = 'sa-text';
            ta.rows = 1;
            ta.setAttribute('aria-label', (o.labels && o.labels.text) || 'Text');
            ta.setAttribute('spellcheck', 'false');
            var fs = clamp(it.w * 6.5, 0.018, 0.12) * Math.min(R.w, R.h) / dpr;
            ta.style.cssText = 'left:' + ((R.x + p.x * R.w) / dpr) + 'px;top:' + ((R.y + p.y * R.h) / dpr) + 'px;font-size:' + Math.max(12, fs) + 'px;color:' + it.c;
            stage.appendChild(ta);
            editor = { ta: ta, it: it, done: false };
            function fit() { ta.style.height = 'auto'; ta.style.height = ta.scrollHeight + 'px'; ta.style.width = Math.max(60, Math.min(stage.clientWidth - 8, ta.scrollWidth + 4)) + 'px'; }
            ta.addEventListener('input', fit);
            ta.addEventListener('keydown', function (e) {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); closeEditor(true); }
                else if (e.key === 'Escape' || e.key === 'Esc') { e.preventDefault(); e.stopPropagation(); closeEditor(false); }
                e.stopPropagation();
            });
            ta.addEventListener('blur', function () { closeEditor(true); });
            // Фокус сразу, в том же нажатии: набор, начатый до следующего тика,
            // уходил странице — буквы становились клавишами инструментов.
            try { ta.focus({ preventScroll: true }); } catch (_) { ta.focus(); }
            fit();
        }
        function closeEditor(commit) {
            if (!editor || editor.done) return;
            var e = editor;
            e.done = true;
            editor = null;
            var t = String(e.ta.value || '').replace(/\s+$/, '');
            if (commit && t) layer.setText(e.it, t);
            else layer.setText(e.it, '');
            layer.end(e.it);
            if (e.ta.parentNode) e.ta.parentNode.removeChild(e.ta);
        }

        // ── панель инструментов ──────────────────────────────────────────
        function q(sel) { return bar ? bar.querySelectorAll(sel) : []; }
        function each(list, fn) { Array.prototype.forEach.call(list, fn); }
        function paintBar() {
            if (!bar) return;
            var zoomed = layer.zoomed();
            each(q('[data-sa-tool]'), function (b) {
                var k = b.getAttribute('data-sa-tool');
                var v = k === layer.tool() || (k === 'zoom' && zoomed);
                b.setAttribute('aria-pressed', v ? 'true' : 'false');
            });
            each(q('[data-sa-color]'), function (b) { var v = b.getAttribute('data-sa-color') === layer.color(); b.setAttribute('aria-pressed', v ? 'true' : 'false'); });
            each(q('[data-sa-size]'), function (b) { var v = b.getAttribute('data-sa-size') === layer.size(); b.setAttribute('aria-pressed', v ? 'true' : 'false'); });
            each(q('[data-sa-act="life"]'), function (b) { b.setAttribute('aria-pressed', layer.life() === 'fade' ? 'true' : 'false'); });
            each(q('[data-sa-act="freeze"]'), function (b) { b.setAttribute('aria-pressed', layer.frozen() ? 'true' : 'false'); });
            each(q('[data-sa-act="scene"]'), function (b) { b.setAttribute('aria-pressed', layer.sceneAuto() ? 'true' : 'false'); });
            each(q('[data-sa-act="undo"]'), function (b) { b.disabled = !layer.canUndo(); });
            each(q('[data-sa-act="redo"]'), function (b) { b.disabled = !layer.canRedo(); });
            each(q('[data-sa-act="clear"]'), function (b) { b.disabled = layer.count() === 0; });
            each(q('[data-sa-act="full"]'), function (b) { b.setAttribute('aria-pressed', ws && ws.active() ? 'true' : 'false'); });
            each(q('[data-sa-frozen]'), function (b) { b.hidden = !layer.frozen(); });
            if (o.onState) { try { o.onState({ on: on, frozen: layer.frozen(), zoomed: zoomed }); } catch (_) {} }
        }
        if (bar) {
            hide.forEach(function (k) { each(q('[data-sa-tool="' + k + '"]'), function (b) { b.hidden = true; }); });
            if (!o.wholeEl) each(q('[data-sa-act="full"]'), function (b) { b.hidden = true; });
            bar.addEventListener('click', function (e) {
                var b = e.target && e.target.closest ? e.target.closest('button') : null;
                if (!b || !bar.contains(b) || b.disabled) return;
                if (b.hasAttribute('data-sa-tool')) pickTool(b.getAttribute('data-sa-tool'), 'bar');
                else if (b.hasAttribute('data-sa-color')) pickColor(b.getAttribute('data-sa-color'));
                else if (b.hasAttribute('data-sa-size')) { layer.setSize(b.getAttribute('data-sa-size')); save('size', layer.size()); }
                else if (b.hasAttribute('data-sa-act')) act(b.getAttribute('data-sa-act'), 'bar');
                paintBar();
            });
        }
        function pickTool(k, via) {
            if (hide.indexOf(k) >= 0) return;
            if (editor) closeEditor(true);
            // Лупа ещё раз — назад к целому кадру.
            if (k === 'zoom' && layer.zoomed() && via !== 'restore') { zoomOut(via); return; }
            if (k === 'zoom' && layer.tool() !== 'zoom') prevTool = layer.tool();
            layer.setTool(k);
            if (k !== 'zoom') save('tool', k);
            cursor();
            paintBar();
            if (via !== 'zoom' && via !== 'restore') layer.tel('annot_tool', { k: k, via: via });
        }
        function pickColor(c) {
            layer.setColor(c);
            save('color', c);
            paintBar();
        }
        function act(a, via) {
            if (a === 'undo') layer.undo();
            else if (a === 'redo') layer.redoOne();
            else if (a === 'clear') layer.clear();
            else if (a === 'freeze') {
                // Стоп-кадр — чтобы рисовать по нему: панель открывается сама
                // (нажатие в окне «Поверх окон» — человек вернётся во вкладку
                // уже к готовой панели).
                if (layer.frozen()) layer.thaw();
                else { layer.freeze(); if (!on) setOn(true, 'freeze'); }
            }
            else if (a === 'life') { layer.setLife(layer.life() === 'fade' ? 'stay' : 'fade'); save('life', layer.life()); layer.tel('annot_life', { life: layer.life() }); }
            else if (a === 'scene') { layer.setSceneAuto(!layer.sceneAuto()); save('scene', layer.sceneAuto() ? '1' : '0'); }
            else if (a === 'save') saveFrame();
            else if (a === 'close') setOn(false, via);
            else if (a === 'help') toggleHelp();
            else if (a === 'full') toggleWhole();
            else if (o.onAct) { try { o.onAct(a, via); } catch (_) {} }
            paintBar();
        }
        function toggleHelp(force) {
            var h = bar ? bar.querySelector('[data-sa-help]') : null;
            if (!h) return;
            h.hidden = force === undefined ? !h.hidden : !force;
        }
        function toggleWhole() {
            if (!window.__wholeScreen || !o.wholeEl) return;
            if (!ws) ws = window.__wholeScreen(o.wholeEl, { key: 'annot', escape: false, onChange: function () { resize(); kick(); paintBar(); } });
            ws.toggle();
        }
        function saveFrame() {
            var c = layer.snapshot();
            if (!c) return;
            var d = new Date(), z = function (n) { return (n < 10 ? '0' : '') + n; };
            var name = 'screen-frame-' + d.getFullYear() + '-' + z(d.getMonth() + 1) + '-' + z(d.getDate()) + '_' + z(d.getHours()) + '-' + z(d.getMinutes()) + '-' + z(d.getSeconds()) + '.png';
            if (!window.__canvasBlob) return;
            window.__canvasBlob(c, 'image/png').then(function (blob) {
                if (!blob) return;
                if (window.__downloadFile) window.__downloadFile(blob, name);
            }).catch(function (e) { try { if (window.__handleError) window.__handleError(e, 'annot_save_frame', { say: o.labels && o.labels.saveFailed }); } catch (_) {} });
        }

        // ── клавиши ──────────────────────────────────────────────────────
        // true — клавиша взята (страница её не трогает).
        function handleKey(e) {
            if (dead || !e || e.defaultPrevented) return false;
            if (editor) return false;
            if (window.__keyOwnedByFocus && window.__keyOwnedByFocus(e)) return false;
            var allowed = o.allowed ? !!o.allowed() : true;
            if (!allowed) return false;
            var L = window.__keyLetter ? window.__keyLetter(e) : String(e.key || '').toLowerCase();
            var chord = e.ctrlKey || e.metaKey;
            if (chord && !e.altKey) {
                if (!on) return false;
                if (L === 'z') { if (e.shiftKey) layer.redoOne(); else layer.undo(); paintBar(); return true; }
                if (L === 'y') { layer.redoOne(); paintBar(); return true; }
                return false;
            }
            if (e.altKey) return false;
            if (L === 'd') { if (!e.repeat) setOn(!on, 'key'); return true; }
            if (L === 'f') { if (!e.repeat) { act('freeze', 'key'); layer.tel('annot_key', { k: 'f' }); } return true; }
            if (!on) return false;
            if (e.key === 'Escape' || e.key === 'Esc') {
                if (zdrag) { zdrag = null; kick(); }
                else if (zoomOut('esc')) { /* сначала лупа */ }
                else if (ws && ws.active()) ws.exit();
                else setOn(false, 'esc');
                return true;
            }
            if (e.key === 'Delete' || e.key === 'Backspace') { layer.clear(); paintBar(); return true; }
            var sign = window.__keySign ? window.__keySign(e) : '';
            if (sign === '?' || (sign === '/' && e.shiftKey)) { toggleHelp(); return true; }
            if (sign === '[' || sign === ']') {
                var order = ['s', 'm', 'l'], i = order.indexOf(layer.size());
                layer.setSize(order[clamp(i + (sign === ']' ? 1 : -1), 0, 2)]);
                save('size', layer.size()); paintBar();
                return true;
            }
            if (/^[1-9]$/.test(L)) {
                var cs = window.ScreenAnnotate.COLORS, ci = (+L) - 1;
                if (ci < cs.length) { pickColor(cs[ci]); return true; }
                return false;
            }
            if (TOOL_KEYS[L] && hide.indexOf(TOOL_KEYS[L]) < 0) {
                if (!e.repeat) pickTool(TOOL_KEYS[L], 'key');
                return true;
            }
            return false;
        }

        // ── включение ────────────────────────────────────────────────────
        function setOn(b, via) {
            b = !!b;
            if (b && o.canTurnOn && !o.canTurnOn()) return;
            if (b === on) { paintBar(); return; }
            on = b;
            if (!on) { if (editor) closeEditor(true); zdrag = null; onUp(); layer.pointer(null); toggleHelp(false); if (ws && ws.active()) ws.exit(); }
            canvas.classList.toggle('on', on);
            stage.classList.toggle('sa-on', on);
            canvas.style.touchAction = on ? 'none' : '';
            canvas.style.pointerEvents = on ? 'auto' : 'none';
            pad.style.touchAction = on ? 'none' : '';
            pad.style.pointerEvents = on ? 'auto' : 'none';
            if (mode === 'frames') layer.setPreview(on || layer.frozen());
            // Фокус — на холст: иначе он остаётся на кнопке «Рисовать» (нажатие
            // по холсту фокус не забирает — pointerdown гасится), и следующий
            // пробел или Enter «нажимал» её снова и закрывал панель (стенд
            // трансляции, 04.11.2026); в записи пробел — ещё и «Пауза».
            if (on) { try { canvas.tabIndex = -1; canvas.focus({ preventScroll: true }); } catch (_) {} }
            cursor();
            needPaint = true;
            kick();
            paintBar();
            layer.tel('annot_mode', { on: on ? 1 : 0, via: via || '' });
            if (o.onToggle) { try { o.onToggle(on, via); } catch (_) {} }
        }
        canvas.style.pointerEvents = 'none';
        pad.style.pointerEvents = 'none';

        // ── восстановить выбор человека ──────────────────────────────────
        (function restore() {
            var t = load('tool', 'pen');
            if (hide.indexOf(t) >= 0 || t === 'zoom') t = 'pen';
            layer.setTool(t);
            var c = load('color', window.ScreenAnnotate.COLORS[0]);
            layer.setColor(c);
            layer.setSize(load('size', 'm'));
            layer.setLife(load('life', 'stay'));
            layer.setSceneAuto(load('scene', '1') !== '0');
            cursor();
            paintBar();
        })();

        return {
            setOn: setOn,
            on: function () { return on; },
            handleKey: handleKey,
            refresh: function () { needPaint = true; kick(); paintBar(); },
            freeze: function () { act('freeze', 'api'); },
            zoomOut: zoomOut,
            canvas: canvas,
            // Элемент ровно над картинкой — для CaptureController.forwardWheel.
            wheelTarget: function () { return pad; },
            destroy: function () {
                dead = true;
                if (raf) { try { win.cancelAnimationFrame(raf); } catch (_) {} }
                if (ro) try { ro.disconnect(); } catch (_) {}
                doc.removeEventListener('visibilitychange', kick);
                if (typeof unlisten === 'function') unlisten();
                if (editor) closeEditor(false);
                if (ws) { try { ws.destroy(); } catch (_) {} }
                if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
                if (pad.parentNode) pad.parentNode.removeChild(pad);
            }
        };
    }

    // ── зритель /screen-share: пометки ведущего поверх СВОЕГО <video> ────
    // Векторы приходят по каналу данных и рисуются в разрешении зрителя —
    // чётко при любом битрейте и раньше, чем дойдёт кадр видео. Холст лежит
    // ровно на коробке <video> (вписанная картинка, «в натуральную величину»,
    // полный экран — всё считается от самого элемента).
    //   o.stage   — контейнер (position:relative), где лежит видео;
    //   o.media   — <video>;
    //   o.onPointer(x, y) / (null) — указка зрителя, когда ведущий её разрешил.
    function viewer(o) {
        var stage = o.stage, media = o.media;
        var cv = document.createElement('canvas');
        cv.className = 'sa-vcanvas';
        cv.setAttribute('aria-hidden', 'true');
        cv.style.cssText = 'position:absolute;left:0;top:0;pointer-events:none;z-index:2';
        stage.appendChild(cv);
        var ctx = cv.getContext('2d');
        var st = { items: [], ptr: null, remote: {}, laserIdleAt: undefined };
        var frozen = null, raf = 0, dead = false, pointing = false, lastSent = 0, pend = null, sendT = 0, box = '';

        // Холст ложится на коробку видео; стиль пишется, только когда коробка
        // сдвинулась, а не на каждый кадр (__perPixel-правило: запись стиля в
        // цикле кадров стоит раскладки страницы).
        function place() {
            var dpr = Math.min(2, window.devicePixelRatio || 1);
            var w = media.offsetWidth, h = media.offsetHeight;
            var key = media.offsetLeft + ',' + media.offsetTop + ',' + w + ',' + h;
            if (key !== box) {
                box = key;
                cv.style.cssText = 'position:absolute;pointer-events:' + (pointing ? 'auto' : 'none') + ';z-index:2;left:' + media.offsetLeft + 'px;top:' + media.offsetTop + 'px;width:' + w + 'px;height:' + h + 'px' + (pointing ? ';cursor:crosshair;touch-action:none' : '');
            }
            var W = Math.max(1, Math.round(w * dpr)), H = Math.max(1, Math.round(h * dpr));
            if (cv.width !== W || cv.height !== H) { cv.width = W; cv.height = H; }
            return C.fitRect(W, H, media.videoWidth || W, media.videoHeight || H);
        }
        function busy(t) {
            return st.items.length > 0 || !!st.ptr || !!frozen || C.remoteList(st, t).length > 0 || !!C.zoomNow(st.zoom, t);
        }
        function paint() {
            raf = 0;
            if (dead) return;
            var R = place(), t = performance.now();
            ctx.clearRect(0, 0, cv.width, cv.height);
            // Лупа ведущего: участок своего же видео (или стоп-кадра) на весь кадр.
            var z = C.zoomNow(st.zoom, t);
            var base = frozen || (z && media.videoWidth ? media : null), bd = dims(base);
            if (base && bd) { try { C.drawZoomed(ctx, base, bd.w, bd.h, z, R.x, R.y, R.w, R.h); } catch (_) {} }
            C.prune(st.items, t, st.laserIdleAt);
            C.render(ctx, R, st.items, t, { laserIdleAt: st.laserIdleAt, ptr: st.ptr, remote: C.remoteList(st, t) });
            if (busy(t) && document.visibilityState !== 'hidden') kick();
        }
        function kick() { if (!raf && !dead) raf = requestAnimationFrame(paint); }
        document.addEventListener('visibilitychange', kick);
        try { new ResizeObserver(kick).observe(media); } catch (_) {}

        function norm(ev) {
            var r = media.getBoundingClientRect();
            var R = C.fitRect(r.width, r.height, media.videoWidth || r.width, media.videoHeight || r.height);
            return C.toNorm(R, ev.clientX - r.left, ev.clientY - r.top, 0);
        }
        function send(p) {
            pend = p;
            var t = performance.now();
            if (t - lastSent >= 33) { flush(); return; }
            if (!sendT) sendT = setTimeout(flush, 33 - (t - lastSent));
        }
        function flush() {
            sendT = 0; lastSent = performance.now();
            try { if (o.onPointer) o.onPointer(pend ? pend.x : null, pend ? pend.y : null); } catch (_) {}
        }
        cv.addEventListener('pointermove', function (ev) { if (pointing) send(norm(ev)); });
        cv.addEventListener('pointerdown', function (ev) { if (pointing) send(norm(ev)); });
        cv.addEventListener('pointerleave', function () { if (pointing) send(null); });

        return {
            apply: function (ops) {
                var t = performance.now();
                for (var i = 0; i < ops.length; i++) C.applyOp(st, ops[i], t);
                kick();
            },
            freeze: function (img) { frozen = img || null; kick(); },
            frozen: function () { return !!frozen; },
            setPointing: function (b) {
                pointing = !!b;
                box = '';   // перестроить стиль холста с новым режимом указателя
                kick();
                if (!pointing) send(null);
            },
            pointing: function () { return pointing; },
            clear: function () { st.items.length = 0; st.ptr = null; st.remote = {}; st.zoom = null; frozen = null; kick(); },
            refresh: kick,
            destroy: function () { dead = true; if (raf) cancelAnimationFrame(raf); if (cv.parentNode) cv.parentNode.removeChild(cv); }
        };
    }

    // ── показываемая вкладка: прокрутка колёсиком и масштаб (CSC) ─────────
    // CaptureController.forwardWheel(el) — колёсико над el уходит в
    // показываемую ВКЛАДКУ (Chrome 136+, только вкладки), точка считается по
    // коробке el — поэтому el = слой ровно над картинкой (wheelTarget), и
    // пересылка снимается, пока картинка в превью — не вся вкладка (лупа,
    // «Область», композит камеры). Первый вызов спрашивает разрешение
    // «captured-surface-control» и обязан идти из нажатия.
    //   o.ctl, o.track — контроллер и видеодорожка этого захвата;
    //   o.surf, o.layer, o.bar; o.valid() — превью показывает вкладку целиком;
    //   o.onDenied(err) — человек не дал разрешения / браузер отказал.
    function surfaceControl(o) {
        var ctl = o.ctl || null, track = o.track || null;
        var want = false, active = false, busy = false;
        var group = o.bar ? o.bar.querySelector('[data-sa-csc]') : null;
        var slug = o.slug || 'screen-recorder';
        function supported() {
            if (!ctl || typeof ctl.forwardWheel !== 'function') return false;
            var surf = '';
            try { surf = (track && track.getSettings().displaySurface) || ''; } catch (_) {}
            return surf === 'browser' && !!track && track.readyState === 'live';
        }
        function tel(f, x) { try { if (window.__ae) window.__ae('feature_use', Object.assign({ slug: slug, f: f }, x || {})); } catch (_) {} }
        function paint() {
            if (!group) return;
            var ok = supported();
            group.hidden = !ok;
            if (!ok) return;
            var b = group.querySelector('[data-sa-act="csc"]');
            if (b) b.setAttribute('aria-pressed', want ? 'true' : 'false');
            var lv = group.querySelector('[data-sa-csc-level]');
            var z = null;
            try { z = ctl.zoomLevel; } catch (_) {}
            if (lv) lv.textContent = (typeof z === 'number' ? z : 100) + '%';
        }
        function sync() {
            var should = want && supported() && !!o.surf && o.surf.on() && !o.layer.zoomed() && (!o.valid || o.valid());
            if (should && !active && !busy) {
                busy = true;
                ctl.forwardWheel(o.surf.wheelTarget()).then(function () { active = true; busy = false; paint(); })
                    .catch(function (e) {
                        busy = false; active = false; want = false; paint();
                        tel('annot_csc_denied', { err: String(e && (e.name || e.message) || '').slice(0, 40) });
                        if (o.onDenied) { try { o.onDenied(e); } catch (_) {} }
                    });
            } else if (!should && active) {
                active = false;
                try { ctl.forwardWheel(null).catch(function () {}); } catch (_) {}
            }
            paint();
        }
        function zoom(fn) {
            if (!supported() || typeof ctl[fn] !== 'function') return;
            try {
                ctl[fn]().then(paint).catch(function (e) {
                    // предел масштаба — не ошибка; отказ в разрешении — объяснить
                    if (e && e.name === 'NotAllowedError' && o.onDenied) { try { o.onDenied(e); } catch (_) {} }
                    paint();
                });
            } catch (_) {}
            tel('annot_csc_zoom', { fn: fn });
        }
        function onZoomChange() { paint(); }
        function attach(c, t) {
            if (ctl && ctl.removeEventListener) { try { ctl.removeEventListener('zoomlevelchange', onZoomChange); } catch (_) {} }
            if (active && ctl) { try { ctl.forwardWheel(null).catch(function () {}); } catch (_) {} }
            ctl = c || null; track = t || null; active = false; busy = false;
            if (ctl && ctl.addEventListener) { try { ctl.addEventListener('zoomlevelchange', onZoomChange); } catch (_) {} }
            sync();
        }
        attach(ctl, track);
        return {
            act: function (a) {
                if (a === 'csc') { want = !want; tel('annot_csc', { on: want ? 1 : 0 }); sync(); return true; }
                if (a === 'csc-in') { zoom('increaseZoomLevel'); return true; }
                if (a === 'csc-out') { zoom('decreaseZoomLevel'); return true; }
                if (a === 'csc-reset') { zoom('resetZoomLevel'); return true; }
                return false;
            },
            sync: sync,
            attach: attach,
            supported: supported,
            active: function () { return active; },
            destroy: function () { want = false; sync(); attach(null, null); }
        };
    }

    // ── окно поверх программ: та же панель в Document Picture-in-Picture ──
    // Человек остаётся в своей программе, а маленькое окно с картинкой и
    // инструментами висит поверх неё. Рисует тот же слой (пометки — в файл и
    // зрителям), окно — лишь ещё одна поверхность. CSS панели переносится
    // из страницы: в окне PiP своего нет.
    function pipSupported() {
        return !!(window.documentPictureInPicture && typeof window.documentPictureInPicture.requestWindow === 'function');
    }
    function panelCss() {
        var out = 'html,body{margin:0;height:100%;background:#05070d;overflow:hidden;font-family:system-ui,-apple-system,"Segoe UI",Roboto,Arial,sans-serif}' +
            '.sa-pipview{position:fixed;inset:0}';
        Array.prototype.forEach.call(document.querySelectorAll('style'), function (st) {
            var t = st.textContent || '';
            if (t.indexOf('.sa-wrap{') >= 0) out += t;
        });
        return out;
    }
    // Новое окно поверх программ (из нажатия). → Promise<Window>
    function openPipWindow(o) {
        o = o || {};
        if (!pipSupported()) return Promise.reject(new Error('no-doc-pip'));
        return window.documentPictureInPicture.requestWindow({ width: o.width || 760, height: o.height || 500 }).then(function (pw) {
            var d = pw.document;
            d.documentElement.lang = o.lang || window.__LOCALE || document.documentElement.lang || 'en';
            d.documentElement.dir = o.dir || document.documentElement.dir || 'ltr';
            var t = d.createElement('title'); t.textContent = o.title || document.title; d.head.appendChild(t);
            return pw;
        });
    }
    //   o.win — окно PiP; o.template — обёртка панели на странице (клонируется);
    //   o.layer, o.mode ('frames' | 'overlay'), o.track (видео для 'overlay');
    //   o.slug, o.labels, o.allowed, o.hideTools, o.onState, o.onAct;
    //   o.keys — слушать клавиши в окне самому (false — их ведёт страница);
    //   o.onClose(why).
    function pipView(o) {
        var win = o.win, doc = win.document;
        if (!doc.querySelector('style[data-sa-pip]')) {
            var st = doc.createElement('style');
            st.setAttribute('data-sa-pip', '1');
            st.textContent = panelCss();
            doc.head.appendChild(st);
        }
        var wrap = o.template.cloneNode(true);
        wrap.removeAttribute('id');
        Array.prototype.forEach.call(wrap.querySelectorAll('.sa-canvas,.sa-pad,.sa-text,.sa-vcanvas'), function (n) { n.parentNode.removeChild(n); });
        Array.prototype.forEach.call(wrap.querySelectorAll('[id]'), function (n) { n.removeAttribute('id'); });
        Array.prototype.forEach.call(wrap.querySelectorAll('[data-sa-act="full"],[data-sa-act="pip"],[data-sa-csc]'), function (n) { n.hidden = true; });
        wrap.classList.add('sa-open');
        var holder = doc.createElement('div');
        holder.className = 'sa-pipview';
        holder.appendChild(doc.adoptNode(wrap));
        doc.body.appendChild(holder);
        var stage = wrap.querySelector('.sa-stage');
        var media = wrap.querySelector('video.sa-media');
        if (o.mode === 'overlay') {
            if (!media) { media = doc.createElement('video'); media.className = 'sa-media'; stage.insertBefore(media, stage.firstChild); }
            media.muted = true; media.playsInline = true; media.autoplay = true;
            try { media.srcObject = new MediaStream([o.track]); var pp = media.play(); if (pp && pp.catch) pp.catch(function () {}); } catch (_) {}
        } else if (media) { media.parentNode.removeChild(media); media = null; }
        var closed = false;
        var surf = mount({
            layer: o.layer, stage: stage, mode: o.mode, media: media, bar: wrap, win: win,
            slug: o.slug, labels: o.labels, hideTools: o.hideTools, allowed: o.allowed, canTurnOn: o.allowed,
            onToggle: function (on) { if (!on) close('panel'); },
            onState: o.onState, onAct: o.onAct
        });
        function onKey(e) { if (surf.handleKey(e)) e.preventDefault(); }
        if (o.keys !== false) doc.addEventListener('keydown', onKey);
        function onGone() { close('window'); }
        win.addEventListener('pagehide', onGone);
        surf.setOn(true, 'pip');
        function close(why) {
            if (closed) return;
            closed = true;
            try { win.removeEventListener('pagehide', onGone); } catch (_) {}
            try { doc.removeEventListener('keydown', onKey); } catch (_) {}
            try { surf.destroy(); } catch (_) {}
            if (media) { try { media.srcObject = null; } catch (_) {} }
            if (holder.parentNode) holder.parentNode.removeChild(holder);
            o.layer.tel('annot_pip', { on: 0, why: why || '' });
            if (o.onClose) { try { o.onClose(why); } catch (_) {} }
        }
        o.layer.tel('annot_pip', { on: 1, mode: o.mode });
        return { surf: surf, close: close, win: win, doc: doc, wrap: wrap, open: function () { return !closed; } };
    }

    window.ScreenAnnotSurface = { mount: mount, viewer: viewer, surfaceControl: surfaceControl, pipSupported: pipSupported, openPipWindow: openPipWindow, pipView: pipView, TOOL_KEYS: TOOL_KEYS };
})();
